import { computed } from "aaa";
import { parse } from "query-string";
import externalInterfaceStore from "stores/ExternalInterfaceStore";

// App Store tracking parameters
const APP_STORE_PROVIDER_TOKEN = "105309866"; // Apple Provider Token for affiliate tracking
const APP_STORE_MEDIA_TYPE = "8"; // Media Type 8 = iOS app

// App Store base URLs
const IOS_APP_STORE_BASE_URL = "https://apps.apple.com/at/app/george-%C3%B6sterreich/id947829367";
const ANDROID_PLAY_STORE_BASE_URL = "https://play.google.com/store/apps/details?id=at.erstebank.george";
const APP_GALLERY_BASE_URL = "https://appgallery.huawei.com/app/C102556323";

export class AppStoreRedirectStore {

    /**
     * Extracts marketing parameters from URL query string and hash
     */
    private getMarketingParams(): {
        campaign?: string;
        medium?: string;
        source?: string;
        institute?: string;
        deeplink?: string;
        ppid?: string;
        listing?: string;
    } {
        const queryParams = parse(window.location.search);
        const hash = window.location.hash;

        // Extract deeplink from hash (e.g., #george-invest)
        const deeplink = hash && hash.startsWith('#') ? hash : undefined;

        return {
            campaign: typeof queryParams.campaign === 'string' ? queryParams.campaign : undefined,
            medium: typeof queryParams.medium === 'string' ? queryParams.medium : undefined,
            source: typeof queryParams.source === 'string' ? queryParams.source : undefined,
            institute: typeof queryParams.institute === 'string' ? queryParams.institute : undefined,
            deeplink: deeplink,
            ppid: typeof queryParams.ppid === 'string' ? queryParams.ppid : undefined,
            listing: typeof queryParams.listing === 'string' ? queryParams.listing : undefined,
        };
    }

    /**
     * Builds iOS App Store URL with ct parameter
     * Combines campaign, medium, source with semicolon delimiter
     */
    private buildIosStoreUrl(baseUrl: string): string {
        const params = this.getMarketingParams();
        const { campaign, medium, source, ppid } = params;

        let url = baseUrl;

        // Only add ct parameter if at least one marketing parameter exists
        if (campaign || medium || source) {
            const ctValue = [campaign, medium, source]
                .filter(Boolean)
                .join(';');

            if (ctValue) {
                // Add pt and mt parameters along with ct parameter
                const separator = url.includes('?') ? '&' : '?';
                url = `${url}${separator}pt=${APP_STORE_PROVIDER_TOKEN}&mt=${APP_STORE_MEDIA_TYPE}&ct=${encodeURIComponent(ctValue)}`;
            }
        }

        if (ppid) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}ppid=${encodeURIComponent(ppid)}`;
        }

        return url;
    }

    /**
     * Builds Android Play Store URL with referrer parameter
     * Includes both UTM and non-UTM parameters, URL-encoded
     */
    private buildAndroidStoreUrl(baseUrl: string): string {
        const params = this.getMarketingParams();
        const { campaign, medium, source, institute, deeplink, listing } = params;

        let url = baseUrl;

        if (listing) {
            url = `${url}&listing=${encodeURIComponent(listing)}`;
        }

        // Only add referrer parameter if at least one marketing parameter exists
        if (campaign || medium || source || institute || deeplink) {
            // Build referrer string with both UTM and non-UTM parameters
            const referrerParts: string[] = [];

            // Add UTM parameters
            if (campaign) referrerParts.push(`utm_campaign=${campaign}`);
            if (medium) referrerParts.push(`utm_medium=${medium}`);
            if (source) referrerParts.push(`utm_source=${source}`);

            // Add non-UTM parameters
            if (campaign) referrerParts.push(`campaign=${campaign}`);
            if (medium) referrerParts.push(`medium=${medium}`);
            if (source) referrerParts.push(`source=${source}`);
            if (institute) referrerParts.push(`institute=${institute}`);
            if (deeplink) referrerParts.push(`deeplink=${deeplink}`);

            if (referrerParts.length > 0) {
                const referrerString = referrerParts.join('&');
                const encodedReferrer = encodeURIComponent(referrerString);
                const separator = url.includes('?') ? '&' : '?';
                url = `${url}${separator}referrer=${encodedReferrer}`;
            }
        }

        return url;
    }

    /**
     * Returns the URL wrapped in a georgeapp:// deep link if the user is in the George app,
     * otherwise returns the URL as-is
     */
    private getUrlWithDeepLinkIfInGeorgeApp(url: string): string {
        return externalInterfaceStore.isGeorgeApp
            ? `georgeapp://at/open_external_link?url=${encodeURIComponent(url)}`
            : url;
    }

    @computed get iosStoreUrl(): string {
        const url = this.buildIosStoreUrl(IOS_APP_STORE_BASE_URL);
        return this.getUrlWithDeepLinkIfInGeorgeApp(url);
    }

    @computed get androidStoreUrl(): string {
        const url = this.buildAndroidStoreUrl(ANDROID_PLAY_STORE_BASE_URL);
        return this.getUrlWithDeepLinkIfInGeorgeApp(url);
    }

    @computed get appGalleryStoreUrl(): string {
        return this.getUrlWithDeepLinkIfInGeorgeApp(APP_GALLERY_BASE_URL);
    }
}



// WEBPACK FOOTER //
// ./src/components/appStoreRedirect/AppStoreRedirectStoreNew.ts